import ApiManager from "./ApiManager";
import {JOB_STATE_COMPLETE, JOB_STATE_FAILED, JOB_STATE_INCOMPLETE} from "../const/job.const";
import {EventBus} from "../events/event.bus";
import {EVENT_JOB_FAIL} from "../events/job.events";
import {useResultPageStore} from "../stores/ResultPageStore";
import {decodeBase64ToImageUrl, encodeImageToBase64} from "../../../../../helper/base64";
import JobManager from "./JobManager";
import Analytics from "../util/Analytics";
import {CONFIG_THUMBNAIL_MAX_INPUT_SIZE, CONFIG_THUMBNAIL_MAX_JOB_SIZE} from "../config/thumbnails.config";

class ThumbnailManager {

    listener = false;

    constructor() {
    }

    generatedThumbnails = false;
    generatedThumbnailsPreview = [];

    async generateThumbnailPreview(job, output) {
        this.store = useResultPageStore();

        if (this.generatedThumbnailsPreview.includes(output.id)) {
            return;
        }

        Analytics.send('result_thumbnail', `generate_thumbnail_preview`, job);

        this.generatedThumbnailsPreview.push(output.id);

        let thumbnailDoneJob = {};

        if (
            JobManager.getExtensionFromOutput(output) === 'jpg' ||
            JobManager.getExtensionFromOutput(output) === 'jpeg' ||
            JobManager.getExtensionFromOutput(output) === 'png' 
        ) {
            if (output.size < 2000000) {
                thumbnailDoneJob = {status: job.apiJob.status, output: [output]};
            } else {
                thumbnailDoneJob = await this._generatePreviewThumbnail(job, output);
            }
        } else {
            thumbnailDoneJob = await this._generatePreviewThumbnail(job, output);
        }

        let base64Thumbnail = await this._getBase64Image(thumbnailDoneJob.output[0].uri);

        job.thumbnails.preview.push({
            apiJob: thumbnailDoneJob,
            output: output.id,
            base64: base64Thumbnail
        })
    }

    async _generatePreviewThumbnail(job, output) {
        let thumbnailJob = await ApiManager.create({'operation': 'system thumbnail preview'});

        await ApiManager.addInputs(thumbnailJob, {source: output.uri, type: 'remote'});

        if (
            JobManager.getExtensionFromOutput(output) === 'jpg' ||
            JobManager.getExtensionFromOutput(output) === 'jpeg' ||
            JobManager.getExtensionFromOutput(output) === 'png'
        ) {
            await ApiManager.start(thumbnailJob, {
                target: 'jpg',
                category: 'image',
                options: {
                    'width': 1100
                },
            }).catch((e) => {
                return;
            });
        } else {
            await ApiManager.start(thumbnailJob, {
                target: 'thumbnail',
                category: 'operation',
                options: {
                    'pages': '1',
                    'width': 1000
                },
            }).catch((e) => {
                return;
            });
        }

        await this._waitUntilComplete(thumbnailJob);

        return await ApiManager.get(thumbnailJob);
    }

    async generateThumbnail(job, outputs) {
        this.store = useResultPageStore();

        if (this.generatedThumbnails === true) {
            return;
        }
        this.generatedThumbnails = true;

        Analytics.send('result_thumbnail', `generate_thumbnail_small`, job);

        let thumbnailJob = await ApiManager.create({'operation': 'systemthumbnailresult'});

        if (null === thumbnailJob) {
            let storeJob = this.store.getJobById(job.id);
            storeJob.thumbnails.small_enabled = false;
            return;
        }

        let thumbInputs = [];
        let sizeJob = 0;

        outputs.forEach((output) => {
            if(output.metadata?.thumbnail_available === false){
                return
            }
            if (output.size < CONFIG_THUMBNAIL_MAX_INPUT_SIZE) {
                sizeJob += output.size;

                if (sizeJob > CONFIG_THUMBNAIL_MAX_JOB_SIZE) {
                    return;
                }

                thumbInputs.push({
                    type: 'remote',
                    source: output.uri,
                });
            }
        });

        if (thumbInputs.length === 0) {
            job.thumbnails.small_enabled = false;

            return;
        }

        await ApiManager.addInputs(thumbnailJob, thumbInputs);

        await ApiManager.start(thumbnailJob, {
            target: 'thumbnail',
            category: 'operation',
            options: {
                'allow_multiple_outputs': true,
                'pages': '1',
                'width': 150
            },
        });

        await this._waitUntilComplete(thumbnailJob);
        let thumbnailDoneJob = await ApiManager.get(thumbnailJob);

        if (null === thumbnailDoneJob) {
            job.thumbnails.small_enabled = false;
            return;
        }

        if (thumbnailDoneJob.status.code === 'failed') {
            job.thumbnails.small_enabled = false;
        }

        for (const output1 of thumbnailDoneJob.output) {
            let originalOutput = this._getOriginalOutputId(job, thumbnailDoneJob, output1);
            let base64Thumbnail = await this._getBase64Image(output1.uri);
            job.thumbnails.small.push({
                apiJob: thumbnailDoneJob,
                output: originalOutput.id,
                base64: base64Thumbnail
            })
        }
    }

    _getOriginalOutputId(originalJob, job, output) {
        let input = job.input.filter((obj) => {
            return obj.id_api === output.source.input[0] || obj.id === output.source.input[0];
        });

        let originalUri = input[0].source;
        let originalOutput = originalJob.apiJob.output.filter((obj) => {
            return obj.uri === originalUri;
        });

        return originalOutput[0];
    }

    getSmallThumbnailByOutput(job, output) {
        this.store = useResultPageStore();
        let storeJob = this.store.getJobById(job.id);

        let storeThumbnail = storeJob.thumbnails.small.find(searchOutput => searchOutput.output === output.id);

        if (typeof storeThumbnail === 'undefined') {
            return;
        }

        if (storeThumbnail.apiJob.output.length === 0) {
            return null;
        }

        return storeThumbnail.base64;
    }

    isSmallThumbnailByOutputLoading(job, output) {
        this.store = useResultPageStore();
        let storeJob = this.store.getJobById(job.id);

        let storeThumbnail = storeJob.thumbnails.small.find(searchOutput => searchOutput.output === output.id);

        if (typeof storeThumbnail === 'undefined') {
            return true;
        }

        if (storeThumbnail.apiJob.status.code === 'failed') {

            return false;
        }

        return storeThumbnail.apiJob.status.code !== 'completed';
    }

    isSmallThumbnailByOutputError(job, output) {
        this.store = useResultPageStore();
        let storeJob = this.store.getJobById(job.id);

        let storeThumbnail = storeJob.thumbnails.small.find(searchOutput => searchOutput.output === output.id);

        if (typeof storeThumbnail === 'undefined') {
            return false;
        }

        if (storeThumbnail.apiJob.status.code === 'failed') {

            return true;
        }

        return false;
    }

    getPreviewThumbnailByOutput(job, output) {
        this.store = useResultPageStore();
        let storeJob = this.store.getJobById(job.id);

        let storeThumbnail = storeJob.thumbnails.preview.find(searchOutput => searchOutput.output === output.id);

        if (typeof storeThumbnail === 'undefined') {
            return;
        }

        if (storeThumbnail.apiJob.output.length === 0) {
            return null;
        }

        return storeThumbnail.base64;
    }

    isPreviewThumbnailByOutputLoading(job, output) {
        this.store = useResultPageStore();
        let storeJob = this.store.getJobById(job.id);

        let storeThumbnail = storeJob.thumbnails.preview.find(searchOutput => searchOutput.output === output.id);

        if (typeof storeThumbnail === 'undefined') {
            return true;
        }

        return storeThumbnail.apiJob.status.code !== 'completed';
    }

    async _waitUntilComplete(job, incomplete = 0) {
        return new Promise((resolve, reject) => {

            async function _waitUntilComplete(job, incomplete = 0) {
                let apiJob = await ApiManager.get(job);
                if (null === apiJob) {
                    resolve();

                    return;
                }

                if (apiJob.status.code === JOB_STATE_FAILED) {
                    EventBus.$emit(EVENT_JOB_FAIL, {});

                    resolve();
                    return;
                }

                if (apiJob.status.code === JOB_STATE_COMPLETE) {
                    resolve();
                    return;
                }

                if (apiJob.status.code === JOB_STATE_INCOMPLETE) {
                    if (incomplete > 60) {
                        job.state = JOB_STATE_FAILED;

                        resolve();
                        return;
                    }

                    setTimeout(() => {
                        _waitUntilComplete(job, ++incomplete);
                    }, 2100);
                    return;
                }

                setTimeout(() => {
                    _waitUntilComplete(job);
                }, 2100);

                return;
            }

            _waitUntilComplete(job, incomplete);
        })
    }

    getExtensionFromOutput(output) {
        let lastDotIndex = output.uri.lastIndexOf('.');
        return output.uri.substring(lastDotIndex + 1);
    }

    async _getBase64Image(url) {
        return new Promise((resolve, reject) => {
            let uri = url;
            encodeImageToBase64(uri).then((base64String) => {
                resolve(decodeBase64ToImageUrl(base64String));
            });
        });
    }
}

export default new ThumbnailManager()